/**
 * Separator - Core Component (Enhanced Wrapper)
 *
 * This component wraps shadcn/ui Separator with additional visual variants,
 * spacing options, and optional label support.
 *
 * Enhancements over shadcn/ui:
 * - Visual variants: default, bold, dotted, dashed
 * - Spacing options: none, sm, md, lg (margin around separator)
 * - Label support: Optional text displayed centered on horizontal separators
 * - Decorative option for accessibility (screen reader handling)
 *
 * Usage:
 * - Use variant="dotted" or "dashed" for stylized dividers
 * - Use label prop for "OR" style dividers in forms
 * - Spacing controls the margin (vertical for horizontal, horizontal for vertical)
 *
 * @see {@link file://./../ui/separator.tsx} for the underlying implementation
 */
import { forwardRef, type ComponentProps } from 'react';
import { Separator as ShadcnSeparator } from '@/app/_components/ui/separator';
import { cn } from '@/app/_lib/utils';

export type SeparatorVariant = 'default' | 'bold' | 'dotted' | 'dashed';
export type SeparatorSpacing = 'none' | 'sm' | 'md' | 'lg';

export interface SeparatorProps
  extends Omit<ComponentProps<'div'>, 'children'> {
  /**
   * The orientation of the separator
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * The visual style variant of the separator
   * @default 'default'
   */
  variant?: SeparatorVariant;
  /**
   * The spacing around the separator
   * @default 'md'
   */
  spacing?: SeparatorSpacing;
  /**
   * Whether the separator is decorative (for screen readers)
   * @default true
   */
  decorative?: boolean;
  /**
   * Label to display with the separator (only for horizontal)
   */
  label?: string;
}

const spacingClasses: Record<SeparatorSpacing, string> = {
  none: '',
  sm: 'my-2',
  md: 'my-4',
  lg: 'my-6',
};

const variantClasses: Record<SeparatorVariant, string> = {
  default: '',
  bold: 'h-0.5 data-[orientation=horizontal]:h-0.5',
  dotted:
    'border-dotted border-t data-[orientation=horizontal]:border-t data-[orientation=vertical]:border-l data-[orientation=horizontal]:h-0 data-[orientation=vertical]:w-0',
  dashed:
    'border-dashed border-t data-[orientation=horizontal]:border-t data-[orientation=vertical]:border-l data-[orientation=horizontal]:h-0 data-[orientation=vertical]:w-0',
};

export const Separator = forwardRef<HTMLDivElement, SeparatorProps>(
  (
    {
      orientation = 'horizontal',
      variant = 'default',
      spacing = 'md',
      decorative = true,
      label,
      className,
      ...props
    },
    ref
  ) => {
    if (label && orientation === 'horizontal') {
      return (
        <div
          ref={ref}
          className={cn(
            'relative flex items-center',
            spacingClasses[spacing]
          )}
          {...props}
        >
          <div className="grow">
            <ShadcnSeparator
              orientation={orientation}
              decorative={decorative}
              className={cn(variantClasses[variant], className)}
            />
          </div>
          <span className="mx-4 shrink text-sm text-muted-foreground">
            {label}
          </span>
          <div className="grow">
            <ShadcnSeparator
              orientation={orientation}
              decorative={decorative}
              className={cn(variantClasses[variant], className)}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          orientation === 'horizontal' ? spacingClasses[spacing] : 'mx-4',
          className
        )}
        {...props}
      >
        <ShadcnSeparator
          orientation={orientation}
          decorative={decorative}
          className={cn(variantClasses[variant], className)}
        />
      </div>
    );
  }
);

Separator.displayName = 'Separator';

export default Separator;
